import { supabase } from "../lib/supabase";

const API_BASE = process.env.REACT_APP_API_BASE || "";

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error("Not signed in. Please log in.");
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

/**
 * @returns {Promise<Array<{
 *   id: string,
 *   slug: string,
 *   name: string,
 *   sourceUrl: string,
 *   enabled: boolean,
 *   sortOrder: number,
 *   defaultMaxDetailUrls: number,
 *   defaultAllowLlmFallback: boolean,
 *   defaultSingleEventDetailPage: boolean,
 *   notes: string | null,
 * }>>}
 */
export async function fetchActivitySources() {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/activity-sources`, {
    method: "GET",
    headers,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Could not load sources (${res.status}).`);
  }

  const list = data.sources;
  if (!Array.isArray(list)) {
    throw new Error("Invalid response from server.");
  }
  return list;
}

/**
 * @param {{ id: string, defaultSingleEventDetailPage: boolean }} payload
 * @returns {Promise<{ id: string, slug: string, name: string, sourceUrl: string, enabled: boolean, sortOrder: number, defaultMaxDetailUrls: number, defaultAllowLlmFallback: boolean, defaultSingleEventDetailPage: boolean, notes: string | null }>}
 */
export async function patchActivitySourceSingleEventMode(payload = {}) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/activity-sources`, {
    method: "PATCH",
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: payload.id,
      defaultSingleEventDetailPage: payload.defaultSingleEventDetailPage === true,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Could not update source (${res.status}).`);
  }
  return data.source;
}

/**
 * @param {{ sourceScope?: string, limit?: number }} [params]
 * @returns {Promise<Array<{
 *   id: string,
 *   sourceScope: string,
 *   canonicalName: string,
 *   status: string,
 *   city: string,
 *   locality: string,
 *   region: string,
 *   countryCode: string,
 *   addressQuality: string,
 *   validationStatus: string,
 *   validationScore: number | null,
 *   validationProvider: string,
 *   validationReason: string,
 *   providerRefs: Array<{
 *     provider: string,
 *     placeId: string,
 *     confidence: number | null,
 *     verifiedAt: string | null,
 *   }>,
 *   aliasCount: number,
 *   createdAt: string | null,
 *   updatedAt: string | null,
 * }>>}
 */
export async function fetchPendingLocationReviews(params = {}) {
  const headers = await authHeaders();
  const qs = new URLSearchParams();
  if (params.sourceScope) qs.set("sourceScope", String(params.sourceScope));
  if (params.limit != null) qs.set("limit", String(params.limit));
  const query = qs.toString();
  const res = await fetch(
    `${API_BASE}/api/admin/location-pending-review${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers,
    }
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Could not load pending locations (${res.status}).`);
  }

  const list = data.items;
  if (!Array.isArray(list)) {
    throw new Error("Invalid response from server.");
  }
  return list;
}

/**
 * @param {{ action: "approve" | "merge" | "reject", locationId: string, mergedIntoLocationId?: string }} payload
 */
export async function runLocationReviewAction(payload = {}) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/admin/location-review-actions`, {
    method: "POST",
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action: payload.action,
      locationId: payload.locationId,
      mergedIntoLocationId: payload.mergedIntoLocationId || "",
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Could not run location review action (${res.status}).`);
  }
  return data;
}
